'use client';
import { useMemo, useEffect } from 'react';
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from 'yup'
import { useForm, useFormContext } from 'react-hook-form';
import { useTabbedForm } from 'src/contexts/era-package/TabbedFormContext'

/**
 * Converts field definitions to React Hook Form default values
 * @param {Array} fields - Array of field definitions
 * @returns {Object} Default values object for React Hook Form
 */
export const createDefaultValues = (fields, initialData) => {
  let generated = fields.reduce((acc, field) => {
    if (field.type === 'table') {
      acc[field.name] = field.defaultValue ?? [];
    } else if (field.type !== 'new') {
      acc[field.name] = field.defaultValue ?? '';
    }
    return acc;
  }, {});

  if (!!initialData)
    return { ...generated, initialData }
  return generated
};

/**
 * Converts field definitions to React Hook Form validation rules
 * @param {Array} fields - Array of field definitions
 * @returns {Object} Validation rules object for React Hook Form
 */
export const createValidationRules = (fields) => {
  return fields.reduce((acc, field) => {
    const rules = {};

    if (field.required && !field.readOnly) {
      rules.required = `${field.label} is required`;
    }

    if (field.maxLength) {
      rules.maxLength = {
        value: field.maxLength,
        message: `${field.label} must not exceed ${field.maxLength} characters`
      };
    }

    if (field.type === 'number') {
      rules.pattern = {
        value: /^[0-9]*$/,
        message: `${field.label} must be a valid number`
      };
    }

    if (field.type === 'email') {
      rules.pattern = {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: `${field.label} must be a valid email address`
      };
    }

    // Add custom validation if provided
    if (field.validate) {
      rules.validate = field.validate;
    }

    if (Object.keys(rules).length > 0) {
      acc[field.name] = rules;
    }

    return acc;
  }, {});
};

export const appendSchema = (base, field) => {
  if (field.type !== 'boolean' && field.maxLength)
    base = base.max(field.type === 'number'
      ? parseInt('9'.repeat(field.maxLength))
      : field.maxLength)
  if (field.required)
    base = base.required()

  return base.label(field.label)
}

const createSchemaFromBasicFields = (fields = [], groupName) => {
  return fields.reduce((acc, field) => {
    if (field.type === 'new')
      return acc
    let base = yup.string()
    if (field.type === 'number') {
      base = yup.number()

    } else if (field.type === 'date')
      base = yup.date()
    else if (field.type === 'table') {
      base = yup.array(yup.object(
        createSchemaFromBasicFields(field.fields)
      ))
    } else if (['boolean', 'toggle'].includes(field.type))
      base = yup.boolean()

    // empty value fix
    base = base.nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value));

    acc[field.name] = appendSchema(base, field)
    return acc
  }, {})
}

const validateFormMemo = async (trigger, customValidation, getValues) => {
  // First run React Hook Form validation
  const isValid = await trigger();

  // Then run custom validation if provided
  if (customValidation && isValid) {
    const formData = getValues();
    return customValidation(formData);
  }

  return isValid;
}

const formatErrors = (errors) => Object.keys(errors).reduce((acc, key) => {
  acc[key] = errors[key]?.message || '';
  return acc;
}, {});

const getError = (fieldName, errors = {}) => {
  let fields = fieldName.split('.')
  let retError = errors
  while (fields.length) {
    let thisField = fields.shift()
    if (retError.hasOwnProperty(thisField))
      retError = retError[thisField]
  }

  if (retError.hasOwnProperty('message')) {
    return retError.message
  }
}
/**
 * 
 * @param {*} formConfig - Additional config to pass to RHF hook.
 * @param {*} defaultValues - Default values for form. Ensure all fields are declared so that `isDirty` will work correctly.
 * @param {(Object) => Yup} schema - Validation schema. Takes in an object and expects a Yup object in return.
 * @param {(FormValues) => boolean} customValidation? - Extra validation to run after RHF `trigger()`.
 * @param {*} fields? - list of fields per `tabKey`. Used for generating basic schema.
 * @returns 
 */
export const useTabbedFormConfig = (formConfig, defaultValues, schema = {}, customValidation, fields) => {

  const validation = useMemo(() => {
    let schemaObj = {}

    Object.keys(defaultValues).forEach(tabKey => {
      let basicSchema = {}, schemaFields = fields?.[tabKey]
      if (!!schemaFields) {
        basicSchema = createSchemaFromBasicFields(schemaFields, tabKey)
      }

      // custom yup validation / modifications to the validation schema per tab
      if (schema.hasOwnProperty(tabKey))
        schemaObj[tabKey] = schema[tabKey](basicSchema)
      else
        schemaObj[tabKey] = yup.object(basicSchema)
    })

    return yup.object(schemaObj).nullable()
  }, [fields, schema, defaultValues])

  const form = useForm({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validation),
    ...formConfig
  });

  const { formState: { isDirty }, watch, trigger, getValues } = form;

  // Custom validation wrapper that integrates with existing validation logic
  const validateForm = useMemo(
    () => validateFormMemo(trigger, customValidation, getValues),
    [trigger, customValidation, getValues]
  );

  return {
    ...form,
    errors: form.formState.errors,
    getError: fieldName => getError(fieldName, form.formState.errors),
    isTabDirty: tabKey => !!form.formState.dirtyFields?.[tabKey],
    hasUnsaved: isDirty,
    validateForm,
    formData: watch(), // Reactive form data
  };
}

/**
 * Custom hook for form configuration that works with existing API
 * @param {Array} allFields - All form fields
 * @param {Function} schema - Custom validation schema - provides the basic schema object, and expects a `yup.object(...)` to be returned.
 * @param {Object} initialData - Initial form data (for edit forms) * 
 * @param {Object} customValidation - Custom validation function
 * @returns {Object} Form methods and state
 */
export const useFormConfig = (allFields, formConfig = {}, schema, initialData = null, customValidation) => {
  const defaultValues = useMemo(() => {
    if (initialData) {
      return { ...createDefaultValues(allFields), ...initialData };
    }
    return createDefaultValues(allFields);
  }, [allFields, initialData]);

  const validation = useMemo(() => {
    const basicSchema = createSchemaFromBasicFields(allFields)

    if (schema)
      return schema(basicSchema)
    return yup.object(basicSchema)
  }, [allFields, schema])

  const form = useForm({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validation),
    ...formConfig
  });

  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    }
  }, [initialData]);

  const { formState: { errors, isDirty }, watch, trigger, getValues } = form;

  // Custom validation wrapper that integrates with existing validation logic
  const validateForm = useMemo(
    () => validateFormMemo(trigger, customValidation, getValues),
    [trigger, customValidation, getValues]
  );

  // Convert React Hook Form errors to the format expected by existing components
  const formattedErrors = useMemo(() => formatErrors(errors), [errors]);

  return {
    ...form,
    errors: formattedErrors,
    hasUnsaved: isDirty,
    validateForm,
    formData: watch(), // Reactive form data
  };
};

/**
 * Hook for accessing form context in child components
 * @returns {Object} Form context methods and state
 */
export const useFormConfigContext = () => {
  const context = useFormContext() || null
  const { isTabbedForm } = useTabbedForm() || {}

  if (!context) {
    console.warn('useFormConfigContext must be used within a FormProvider');
    return null
  }

  const { formState: { errors, isDirty }, watch } = context;

  // Convert React Hook Form errors to the format expected by existing components ({[fieldName]: 'message'})
  const formattedErrors = formatErrors(errors)

  return {
    ...context,
    errors: formattedErrors,
    getError: fieldName => getError(fieldName, context.formState.errors),
    isTabDirty: tabKey => isTabbedForm ? !!context.formState.dirtyFields?.[tabKey] : false,
    hasUnsaved: isDirty,
    formData: watch(), // Reactive form data
  };
};